import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Ban, ChevronLeft, Mail, Plus, RefreshCw, RotateCcw, UserCog, X } from 'lucide-react';
import { adminApi, AdminUser, InviteAdminRequest } from '../api/admin';
import { getErrorMessage } from '../api/client';
import { useAdminAuthStore } from '../store/adminAuthStore';

const ROLES: InviteAdminRequest['role'][] = ['Admin', 'Compliance', 'Operations', 'Viewer'];
const MANAGEABLE_ROLES: InviteAdminRequest['role'][] = ['Admin', 'Compliance', 'Operations', 'Viewer'];

function statusClass(status: string) {
  switch (status) {
    case 'Active':
      return 'bg-green-100 text-green-800';
    case 'PendingInvitation':
      return 'bg-amber-100 text-amber-900';
    case 'Suspended':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}

function formatStatus(status: string) {
  if (status === 'PendingInvitation') return 'Pending invite';
  return status;
}

function TeamPage() {
  const currentAdmin = useAdminAuthStore((state) => state.adminUser);
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [updatingAdminId, setUpdatingAdminId] = useState<string | null>(null);
  const [form, setForm] = useState<InviteAdminRequest>({ email: '', role: 'Admin', message: '' });
  const canManageAccess = currentAdmin?.role === 'SuperAdmin';

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.getAdmins();
      setAdmins(res.data || []);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not load admin users.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const sendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await adminApi.inviteAdmin({
        email: form.email.trim(),
        role: form.role,
        message: form.message?.trim() || undefined,
      });
      setNotice(`Invitation sent to ${form.email.trim()}.`);
      setModalOpen(false);
      setForm({ email: '', role: 'Admin', message: '' });
      await load();
    } catch (err) {
      setError(getErrorMessage(err, 'Could not send invitation.'));
    } finally {
      setSubmitting(false);
    }
  };

  const resend = async (admin: AdminUser) => {
    setError(null);
    try {
      await adminApi.inviteAdmin({ email: admin.email, role: admin.role });
      setNotice(`Invitation resent to ${admin.email}.`);
      await load();
    } catch (err) {
      setError(getErrorMessage(err, 'Could not resend invitation.'));
    }
  };

  const updateAccess = async (admin: AdminUser, action: 'suspend' | 'reactivate') => {
    const actionLabel = action === 'suspend' ? 'revoke access for' : 'reset access and send a new invitation to';
    if (!window.confirm(`Are you sure you want to ${actionLabel} ${admin.email}?`)) return;

    setUpdatingAdminId(admin.id);
    setError(null);
    try {
      if (action === 'suspend') {
        await adminApi.suspendAdmin(admin.id);
        setNotice(`Access revoked for ${admin.email}.`);
      } else {
        await adminApi.reactivateAdmin(admin.id);
        setNotice(`Access reset and a new invitation was sent to ${admin.email}.`);
      }
      await load();
    } catch (err) {
      setError(getErrorMessage(err, `Could not ${action === 'suspend' ? 'revoke' : 'restore'} access.`));
    } finally {
      setUpdatingAdminId(null);
    }
  };

  const updateRole = async (admin: AdminUser, role: InviteAdminRequest['role']) => {
    if (role === admin.role) return;
    if (!window.confirm(`Change ${admin.email}'s role from ${admin.role} to ${role}?`)) return;

    setUpdatingAdminId(admin.id);
    setError(null);
    try {
      await adminApi.updateAdminRole(admin.id, { role });
      setNotice(`Role updated for ${admin.email}.`);
      await load();
    } catch (err) {
      setError(getErrorMessage(err, 'Could not update the admin role.'));
    } finally {
      setUpdatingAdminId(null);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <Link to="/settings" className="mb-1 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-indigo-600">
            <ChevronLeft size={14} />
            Settings
          </Link>
          <h1 className="text-2xl font-semibold text-gray-900">Team</h1>
          <p className="text-gray-600">Invite staff to the admin panel. They complete registration from the email link.</p>
        </div>
        {canManageAccess && (
          <button
            type="button"
            onClick={() => {
              setError(null);
              setModalOpen(true);
            }}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
          >
            <Plus size={16} />
            Invite admin
          </button>
        )}
      </div>

      {notice && (
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          {notice}
        </div>
      )}
      {error && !modalOpen && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
      )}

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">User</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Role</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Invited</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && admins.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500">
                  No admin users yet.
                </td>
              </tr>
            )}
            {!loading &&
              admins.map((admin) => {
                const name = `${admin.firstName || ''} ${admin.lastName || ''}`.trim();
                return (
                  <tr key={admin.id}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-50 text-indigo-700">
                          <UserCog size={16} />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{name || admin.email}</p>
                          <p className="text-xs text-gray-500">{admin.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {canManageAccess && admin.id !== currentAdmin?.id && admin.role !== 'SuperAdmin' ? (
                        <select
                          value={admin.role}
                          disabled={updatingAdminId === admin.id}
                          onChange={(event) => void updateRole(admin, event.target.value as InviteAdminRequest['role'])}
                          className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
                          aria-label={`Change role for ${admin.email}`}
                        >
                          {MANAGEABLE_ROLES.map((role) => (
                            <option key={role} value={role}>{role}</option>
                          ))}
                        </select>
                      ) : (
                        admin.role
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(admin.status)}`}>
                        {formatStatus(admin.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {admin.invitedAt ? new Date(admin.invitedAt).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {canManageAccess && admin.id !== currentAdmin?.id && admin.status === 'Active' && (
                        <button
                          type="button"
                          disabled={updatingAdminId === admin.id}
                          onClick={() => void updateAccess(admin, 'suspend')}
                          className="mr-3 inline-flex items-center gap-1 text-sm text-red-600 hover:text-red-800 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <Ban size={14} />
                          Revoke access
                        </button>
                      )}
                      {canManageAccess && admin.status === 'Suspended' && (
                        <button
                          type="button"
                          disabled={updatingAdminId === admin.id}
                          onClick={() => void updateAccess(admin, 'reactivate')}
                          className="mr-3 inline-flex items-center gap-1 text-sm text-green-700 hover:text-green-800 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <RotateCcw size={14} />
                          Reset access & send invite
                        </button>
                      )}
                      {canManageAccess && admin.status === 'PendingInvitation' && (
                        <button
                          type="button"
                          onClick={() => void resend(admin)}
                          className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800"
                        >
                          <RefreshCw size={14} />
                          Resend
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
              <h2 className="text-lg font-semibold text-gray-900">Invite admin</h2>
              <button type="button" onClick={() => setModalOpen(false)} className="rounded p-1 text-gray-400 hover:bg-gray-100">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={sendInvite} className="space-y-4 px-5 py-4">
              {error && modalOpen && (
                <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <div className="mt-1 relative">
                  <Mail className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                    className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                    placeholder="name@xchangelab.com"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Role</label>
                <select
                  value={form.role}
                  onChange={(e) => setForm((p) => ({ ...p, role: e.target.value }))}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                >
                  {ROLES.map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Message <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <textarea
                  rows={3}
                  value={form.message}
                  onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="rounded-md px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  {submitting ? 'Sending…' : 'Send invitation'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default TeamPage;
